/**
 * Property Search Component
 * Search for elements by property values
 */

import React, { useState } from 'react';
import { useViewerStore } from '@/store/viewerStore';
import { usePropertiesStore } from '@/store/propertiesStore';
import { PropertiesService } from '@/services/core/propertiesTools';
import { ViewerService } from '@/services/core/viewerTools';

export function PropertySearch() {
  const { viewer } = useViewerStore();
  const { setSearchFilter } = usePropertiesStore();
  
  const [searchMode, setSearchMode] = useState<'filter' | 'global'>('filter');
  const [propertyName, setPropertyName] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [exactMatch, setExactMatch] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);

  const handleFilterSearch = (value: string) => {
    setSearchFilter(value);
  };

  const handleGlobalSearch = async () => {
    if (!viewer || !propertyName || !searchValue) {
      setSearchError('Please enter property name and value');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setSearchResults([]);

    try {
      const results = await PropertiesService.searchByProperty(
        viewer,
        propertyName,
        searchValue,
        exactMatch
      );

      setSearchResults(results);

      if (results.length > 0) {
        // Highlight all found elements
        ViewerService.highlightElements(viewer, results);
      } else {
        setSearchError('No elements found with this property value');
      }
    } catch (err) {
      setSearchError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    setPropertyName('');
    setSearchValue('');
    setSearchResults([]);
    setSearchError(null);
    setSearchFilter('');
    if (viewer) {
      ViewerService.clearHighlights(viewer);
    }
  };

  return (
    <div className="mb-4 p-3 rounded-lg bg-dark-200 border border-gray-700">
      <div className="mb-3">
        <label className="text-sm font-medium text-gray-400">Search Mode</label>
        <div className="flex gap-2 mt-1">
          <button
            className={`btn btn-small ${searchMode === 'filter' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setSearchMode('filter')}
          >
            Filter Current Properties
          </button>
          <button
            className={`btn btn-small ${searchMode === 'global' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setSearchMode('global')}
          >
            Search All Elements
          </button>
        </div>
      </div>

      {searchMode === 'filter' ? (
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1">
            Filter Properties
          </label>
          <input
            type="text"
            className="input-dark w-full"
            placeholder="Type to filter property names or values..."
            onChange={(e) => handleFilterSearch(e.target.value)}
          />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Property Name
              </label>
              <input
                type="text"
                className="input-dark w-full"
                placeholder="e.g., Type, Level, Material"
                value={propertyName}
                onChange={(e) => setPropertyName(e.target.value)}
                disabled={isSearching}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Property Value
              </label>
              <input
                type="text"
                className="input-dark w-full"
                placeholder="Value to search for"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                disabled={isSearching}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={exactMatch}
                onChange={(e) => setExactMatch(e.target.checked)}
                disabled={isSearching}
              />
              <span className="text-sm text-gray-100">Exact match</span>
            </label>
          </div>

          <div className="flex gap-2">
            <button
              className="btn btn-primary flex-1"
              onClick={handleGlobalSearch}
              disabled={isSearching || !propertyName || !searchValue}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleClearSearch}
              disabled={isSearching}
            >
              Clear
            </button>
          </div>

          {/* Search Results */}
          {searchError && (
            <div className="p-2 rounded bg-red-900/20 border border-red-800">
              <p className="text-sm text-red-400">{searchError}</p>
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="p-2 rounded bg-green-900/20 border border-green-800">
              <p className="text-sm text-green-400">
                Found {searchResults.length} element{searchResults.length !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Elements are highlighted in the viewer
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}